import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchExerciseSchedule } from "../../../store/slices/exerciseSchedulesSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function ExerciseFormPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { currentExerciseSchedule, isLoading, error } = useSelector((state) => state.exerciseSchedules || {});

    useEffect(() => {
        if (id) dispatch(fetchExerciseSchedule(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentExerciseSchedule) {
            const timer = setTimeout(() => window.print(), 1000);
            window.onafterprint = () => navigate(-1);
            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentExerciseSchedule, navigate]);

    if (isLoading) return <div className="p-8 text-center">Loading Exercise Schedule...</div>;
    if (error) return <div className="p-8 text-danger text-center">{error}</div>;
    if (!currentExerciseSchedule) return <div className="p-8 text-center">No record found.</div>;

    const d = currentExerciseSchedule;

    const fmtDate = (dateStr) =>
        dateStr ? new Date(dateStr).toLocaleDateString("en-GB", {
            day: "2-digit", month: "short", year: "numeric",
        }) : "";

    // Mapping definition based on your backend response keys
    const physicalActivities = [
        { label: "Stretching Exercise", key: "phys_stretching" },
        { label: "Walking", key: "phys_walking" },
        { label: "Strengthening Exercise", key: "phys_strengthening" }
    ];

    const gymExercises = [
        { label: "Tread Mill", key: "gym_tread" },
        { label: "Bi-Cycle", key: "gym_bicycle" },
        { label: "Any Sport Activity", key: "gym_anysport" }
    ];

    return (
        <>
            <style jsx global>{`
                * { box-sizing: border-box !important; font-family: Arial, sans-serif !important; }
                body { background: #fff !important; color: #000 !important; font-size: 10px !important; margin: 0 !important; padding: 0 !important; }
                .print-container { padding: 25px !important; position: relative !important; }
                ${printFooterStyles}
                .logo { position: absolute !important; top: 10px !important; left: 10px !important; }
                .logo img { height: 55px !important; width: auto !important; }
                
                .header { text-align: center !important; margin-bottom: 15px !important; border-bottom: 2px solid #000 !important; padding-bottom: 10px !important; }
                .header h3 { font-size: 1.4rem !important; font-weight: bold !important; margin-bottom: 2px !important; }
                .header h6 { font-size: 0.9rem !important; text-transform: uppercase !important; }

                .meta-table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 15px !important; }
                .meta-table td { border: 1px solid #000 !important; padding: 5px 8px !important; }
                .label-bg { background: #f2f2f2 !important; font-weight: bold !important; width: 15% !important; }

                .schedule-table { width: 100% !important; border-collapse: collapse !important; margin-top: 5px !important; table-layout: fixed !important; }
                .schedule-table th, .schedule-table td { border: 1px solid #000 !important; padding: 4px 2px !important; text-align: center !important; word-wrap: break-word !important; vertical-align: middle !important; }
                .schedule-table th { background: #eee !important; font-weight: bold !important; font-size: 9px !important; height: 30px !important; }
                
                .sub-header { background: #f9f9f9 !important; font-weight: bold !important; font-size: 8px !important; }
                .done-mark { font-weight: bold !important; color: #000 !important; font-size: 12px !important; }
                .not-done { color: #666 !important; font-size: 8px !important; }

                .footer-meta { width: 100% !important; border-collapse: collapse !important; margin-top: 20px !important; font-size: 9px !important; }
                .footer-meta td { border: 1px solid #000 !important; padding: 4px !important; }

                .footer-brand { margin-top: 25px !important; border-top: 1px solid #000 !important; padding-top: 8px !important; text-align: center !important; font-size: 9px !important; }
                
                @media print {
                    .schedule-table tr { page-break-inside: avoid !important; }
                }
            `}</style>

            <PrintWrapper>
                <div className="print-container">
                <div className="logo"><img src={logo} alt="Logo" /></div>
                <div className="header">
                    <h3>THE RECOVERY HOUSE</h3>
                    <h6>WEEKLY CLIENTS EXERCISE SCHEDULE</h6>
                </div>

                <table className="meta-table">
                    <tbody>
                        <tr>
                            <td className="label-bg">Client Name</td>
                            <td>{d.client_name}</td>
                            <td className="label-bg">Weight</td>
                            <td>{d.weight} kg</td>
                            <td className="label-bg">BMI</td>
                            <td>{d.bmi}</td>
                        </tr>
                        <tr>
                            <td className="label-bg">Goal</td>
                            <td>{d.goal}</td>
                            <td className="label-bg">Staff</td>
                            <td>{d.responsible_staff}</td>
                            <td className="label-bg">Plan</td>
                            <td>{d.plan}</td>
                        </tr>
                        <tr>
                        </tr>
                    </tbody>
                </table>

                <table className="schedule-table">
                    <thead>
                        <tr>
                            <th rowSpan="2" style={{ width: '70px' }}>Date / Time</th>
                            <th colSpan="3">Physical Activities</th>
                            <th colSpan="3">Gym Exercises</th>
                            <th rowSpan="2">Weekly Outcome</th>
                            <th rowSpan="2" style={{ width: '70px' }}>Staff</th>
                        </tr>
                        <tr className="sub-header">
                            {physicalActivities.map(item => <th key={item.key}>{item.label}</th>)}
                            {gymExercises.map(item => <th key={item.key}>{item.label}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {(d.rows || []).map((row, idx) => (
                            <tr key={idx}>
                                <td style={{ fontSize: '8px' }}>
                                    {fmtDate(row.date)}<br />{row.time}
                                </td>

                                {/* Physical Activities Mapping */}
                                {physicalActivities.map(act => {
                                    const status = row[act.key]; // e.g. row['phys_stretching']
                                    const duration = row[`${act.key}_duration`];
                                    const reason = row[`${act.key}_reason`];
                                    return (
                                        <td key={act.key}>
                                            {status === "yes" ? (
                                                <div><span className="done-mark">✓</span><br />{duration}</div>
                                            ) : status === "no" ? (
                                                <div className="not-done">X<br />{reason}</div>
                                            ) : "-"}
                                        </td>
                                    );
                                })}

                                {/* Gym Exercises Mapping */}
                                {gymExercises.map(gym => {
                                    const status = row[gym.key]; // e.g. row['gym_tread']
                                    const duration = row[`${gym.key}_duration`];
                                    const reason = row[`${gym.key}_reason`];
                                    return (
                                        <td key={gym.key}>
                                            {status === "yes" ? (
                                                <div><span className="done-mark">✓</span><br />{duration}</div>
                                            ) : status === "no" ? (
                                                <div className="not-done">X<br />{reason}</div>
                                            ) : "-"}
                                        </td>
                                    );
                                })}

                                <td style={{ fontSize: '8px', textAlign: 'left' }}>{row.weekly_outcome}</td>
                                <td style={{ fontSize: '8px' }}>{row.staff_name}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <table className="footer-meta">
                    <tbody>
                        <tr>
                            <td className="label-bg">Document Name</td>
                            <td>{d.document_name}</td>
                            <td className="label-bg">Effective Date</td>
                            <td>{fmtDate(d.effective_date)}</td>
                            <td className="label-bg">Approved By</td>
                            <td>{d.approved_by}</td>
                        </tr>
                        <tr>
                            <td className="label-bg">Approved Date</td>
                            <td>{fmtDate(d.approved_date)}</td>
                            <td className="label-bg">Next Revision</td>
                            <td>{d.next_revision_by}</td>
                            <td className="label-bg">Revision Date</td>
                            <td>{fmtDate(d.next_revision_date)}</td>
                        </tr>
                    </tbody>
                </table>

                <div className="footer-brand">
                    <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph: 021-34546364-65-66</p>
                    <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
                </div>
            </div>
            </PrintWrapper>
        </>
    );
}